import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { style } from 'typestyle';
import DatePicker from 'react-date-picker';
import Switch from 'react-switch';
import { TextField } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import moment from 'moment';
import type Announcement from '../models/announcement';
import { IsMobileContext } from '../../../core/context/is-mobile';
import * as styles from './modal.styles';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import Row from '../../../core/components/row/row.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import AnnouncementFormType from '../enums/announcement-form-types';
import StyleConstants from '../../../core/styles.constants';
import ConfirmationModal from './confirmation-modal.component';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly initialAnnouncement: Announcement;
  readonly handleSave: (originalAnnouncement: Announcement, changedAnnouncement: Announcement) => Promise<boolean>;
  readonly announcementIsValid: (value: Announcement) => boolean;
  readonly onDelete: (value: Announcement) => Promise<boolean>;
}

const EditAnnouncementModal = ({
  showModal,
  handleClose,
  initialAnnouncement,
  handleSave,
  onDelete,
  announcementIsValid,
}: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [updateAnnouncement, setUpdatedAnnouncement] = useState<Announcement>(initialAnnouncement);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleCancelModal = (): void => {
    handleClose();
  };

  const handleAnnouncementChange = (formType: AnnouncementFormType, value: unknown): void => {
    const announcementCopy = { ...updateAnnouncement };
    switch (formType) {
      case AnnouncementFormType.message:
        announcementCopy.message = value as string;
        break;
      case AnnouncementFormType.startDate:
        announcementCopy.startDate = moment
          .utc(value as Date)
          .add((value as Date).getTimezoneOffset() * -1, 'm')
          .toDate() as Date;
        break;
      case AnnouncementFormType.endDate:
        if (value === undefined) {
          announcementCopy.endDate = value as undefined;
        } else {
          announcementCopy.endDate = moment(value).endOf('day').toDate();
        }
        break;
      case AnnouncementFormType.isNonWorkingDay:
        announcementCopy.isNonWorkingDay = value as boolean;
        break;
      default:
        break;
    }
    setUpdatedAnnouncement(announcementCopy);
  };

  const handleUpdate = async (): Promise<void> => {
    if (isSaving) {
      return;
    }

    if (announcementIsValid(updateAnnouncement)) {
      setIsSaving(true);
      const success = await handleSave(initialAnnouncement, updateAnnouncement);
      setIsSaving(false);
      if (success) {
        handleClose();
      }
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (isDeleting) {
      return;
    }

    setIsDeleting(true);
    const success = await onDelete(initialAnnouncement);
    setIsDeleting(false);
    setShowDeleteModal(false);
    if (success) {
      handleClose();
    }
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleCancelModal} style={styles.modalStyle(isMobile, 800, 'visible')}>
      <Column className={style(styles.modalBody(isMobile))} itemSpacing={28} mainAxisAlignment={MainAxisAlignment.Stretch}>
        <Row>
          <span className={style(styles.columnHeader(isMobile, 24))}>{t('edit-announcement')}</span>
        </Row>
        <Row mainAxisAlignment={MainAxisAlignment.Stretch}>
          <TextField
            label={t('message').toString()}
            multiline
            maxRows={2}
            value={updateAnnouncement.message}
            onChange={(event): void => {
              handleAnnouncementChange(AnnouncementFormType.message, event.target.value);
            }}
          />
        </Row>

        <Row crossAxisAlignment={CrossAxisAlignment.Center} mainAxisAlignment={MainAxisAlignment.SpaceBetween}>
          <span className={style(styles.modalText)}>{t('choose-the-start-day')}</span>
          <DatePicker
            clearIcon={null}
            allowPartialRange
            calendarClassName={style(styles.calendar)}
            format="dd/MM/yyyy"
            value={updateAnnouncement.startDate}
            onChange={(value): void => {
              if (value instanceof Date) {
                handleAnnouncementChange(AnnouncementFormType.startDate, value);
              }
            }}
          />

          <span className={style(styles.modalText)}>{t('choose-the-end-day')}</span>
          <DatePicker
            clearIcon={null}
            calendarClassName={style(styles.calendar)}
            format="dd/MM/yyyy"
            value={updateAnnouncement.endDate}
            onChange={(value): void => {
              if (value instanceof Date) {
                handleAnnouncementChange(AnnouncementFormType.endDate, value);
              }
            }}
          />
        </Row>

        <Row crossAxisAlignment={CrossAxisAlignment.Center} itemSpacing={10}>
          <Switch
            checked={updateAnnouncement.isNonWorkingDay}
            onChange={(value: boolean): void => {
              handleAnnouncementChange(AnnouncementFormType.isNonWorkingDay, value);
            }}
            onColor={StyleConstants.ORANGE_COLOR}
            onHandleColor={StyleConstants.WHITE_COLOR}
            handleDiameter={styles.switchDiameter}
            uncheckedIcon={false}
            checkedIcon={false}
            boxShadow={styles.switchBoxShadow}
            activeBoxShadow={styles.switchActiveBoxShadow}
            height={styles.switchHeight}
            width={styles.switchWidth}
          />
          <span className={style(styles.modalText)}>{t('is-non-working-day')}</span>
        </Row>

        <Row
          className={style(styles.deleteButton)}
          crossAxisAlignment={CrossAxisAlignment.Center}
          onClick={(): void => {
            setShowDeleteModal(true);
          }}
        >
          <span>{t('delete-announcement')}</span>
          <DeleteOutlineIcon />
        </Row>

        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              loading={isSaving}
              fullWidth
              onClick={(): void => {
                handleUpdate();
              }}
            >
              {t('save')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleCancelModal}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
      <ConfirmationModal
        showModal={showDeleteModal}
        itemName={t('announcement')}
        isLoading={isDeleting}
        handleAccept={(): void => {
          handleDelete();
        }}
        handleClose={(): void => {
          setShowDeleteModal(false);
        }}
      />
    </Modal>
  );
};

export default EditAnnouncementModal;
